import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  canSeeInvoiceInboxFor,
  fetchInvoiceApprovalsQueue,
} from "features/invoice-approvals/invoice-approvals.slice";
import { approvePurchaseDoc, rejectPurchaseDoc } from "services/invoice-inbox.service";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { InvoiceWaitingDoc } from "types/InvoiceInbox";
import { formatAmount, isDueDatePast } from "utils/invoiceInboxStatus";
import { formatPrettyDate } from "utils/helperFunctions";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import useIsPhone from "features/files/use-is-phone";

/**
 * "Sąskaitų tvirtinimas" — the task list page listing purchase invoices waiting
 * on the CURRENT user's decision (an accountant or a manager slot,
 * `myRole`), with approve/reject inline so a routine invoice never needs the
 * whole register open. Its data lives in the `invoice-approvals` slice.
 */

/** Maps a failed approve/reject to the same copy the register's bulk bar
 * uses for the same server codes — `code` when the app has a translation
 * for it, else the server's own message, else a generic fallback. */
function describeActionError(error: any, t: (key: string, opts?: any) => string, exists: (key: string) => boolean): string {
  const data = error?.response?.data;
  const code = data?.code;
  if (code) {
    const key = `invoice_inbox_bulk_error_${code}`;
    if (exists(key)) return t(key);
  }
  return data?.message || data?.code || t("invoice_inbox_action_failed");
}

/** One line for a confirm() or a toast — the supplier name is text the
 * model read off the PDF and may carry newlines, which in a confirm dialog
 * could push the amount out of sight. */
function oneLine(value: string | null | undefined): string {
  return String(value || "").replace(/\s+/g, " ").trim() || "—";
}

export default function InvoiceApprovalsQueue() {
  const { companyId } = useParams<{ companyId: string }>();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const role = useAppSelector((state) => state.authReducer.role);
  const {
    companyId: sliceCompanyId,
    docs: sliceDocs,
    isLoading,
    isError,
  } = useAppSelector((state) => state.invoiceApprovalsReducer);

  const canSee = canSeeInvoiceInboxFor(role, companyId);
  // Never show a previous company's rows while this one's fetch is in
  // flight — the slice clears them itself on a company switch, but the
  // guard costs nothing and holds even before that dispatch lands.
  const docs: InvoiceWaitingDoc[] = sliceCompanyId === companyId ? sliceDocs : [];

  const reload = useCallback(() => {
    if (companyId && canSee) {
      dispatch(fetchInvoiceApprovalsQueue(companyId));
    }
  }, [companyId, canSee, dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Busy/error state is per row, keyed by doc id — one slow approval must
  // never block the rest of the queue, and one finishing must not re-enable
  // another that is still in flight (a Set, not a single id).
  const [busyIds, setBusyIds] = useState<Set<string>>(() => new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      if (prev.has(id) === busy) return prev;
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  // A failed decision is reported in the row AND as a toast: the reload that
  // follows may drop the row (the server invalidated the slots, or someone
  // else decided first), and an error that vanishes with its row reads as
  // success — the queue would just say "nothing waiting".
  const reportFailure = (doc: InvoiceWaitingDoc, error: any) => {
    const message = describeActionError(error, t, (key) => (i18n as any).exists(key));
    setRowErrors((prev) => ({ ...prev, [doc.id]: message }));
    toast.error(`${oneLine(doc.invoiceNumber) !== "—" ? oneLine(doc.invoiceNumber) : `#${doc.docNumber}`} · ${oneLine(doc.supplierNameRaw)}: ${message}`);
  };
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const clearRowError = (id: string) =>
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const handleApprove = async (doc: InvoiceWaitingDoc) => {
    if (!companyId) return;
    const confirmed = window.confirm(
      t("invoice_approvals_approve_confirm", {
        number: doc.invoiceNumber ? oneLine(doc.invoiceNumber) : `#${doc.docNumber}`,
        supplier: oneLine(doc.supplierNameRaw),
        amount: formatAmount(doc.grossAmount, doc.currency),
      })
    );
    if (!confirmed) return;
    setBusy(doc.id, true);
    clearRowError(doc.id);
    try {
      await approvePurchaseDoc(companyId, doc.id);
    } catch (e) {
      reportFailure(doc, e);
    } finally {
      setBusy(doc.id, false);
      // The server may already have moved the document (someone else acted
      // on it first) — a re-fetch is the only way to know where it landed.
      reload();
      dispatch(fetchCabinetSummaryThunk(companyId));
    }
  };

  const openReject = (id: string) => {
    setRejectingId(id);
    setRejectReason("");
  };

  const cancelReject = () => {
    setRejectingId(null);
    setRejectReason("");
  };

  const handleConfirmReject = async (doc: InvoiceWaitingDoc) => {
    if (!companyId || !rejectReason.trim()) return;
    setBusy(doc.id, true);
    clearRowError(doc.id);
    try {
      await rejectPurchaseDoc(companyId, doc.id, rejectReason.trim());
      setRejectingId(null);
      setRejectReason("");
    } catch (e) {
      reportFailure(doc, e);
    } finally {
      setBusy(doc.id, false);
      reload();
      dispatch(fetchCabinetSummaryThunk(companyId));
    }
  };

  const decisionLabel = (myRole: InvoiceWaitingDoc["myRole"]) => {
    if (myRole === "manager") return t("invoice_approvals_role_manager");
    if (myRole === "accountant") return t("invoice_approvals_role_accountant");
    return myRole;
  };

  // Open / approve / reject for one invoice. In the table they sit in a row; on a phone
  // they are three equal 44 px buttons side by side under the invoice card.
  const renderActions = (doc: InvoiceWaitingDoc, rowBusy: boolean) => (
    <>
      <div className="flex items-center gap-2 max-md:grid max-md:grid-cols-3 max-md:gap-3">
        <Link
          to={`/app/${companyId}/invoice-inbox?tab=awaiting_approval&doc=${doc.id}`}
          className="text-blue-600 hover:underline max-md:flex max-md:min-h-11 max-md:items-center max-md:justify-center max-md:rounded-sm max-md:border max-md:border-blue-200 max-md:text-sm"
        >
          {t("invoice_approvals_open")}
        </Link>
        <button
          type="button"
          disabled={rowBusy}
          onClick={() => handleApprove(doc)}
          className="rounded-sm bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 max-md:min-h-11 max-md:text-sm"
        >
          {t("invoice_approvals_approve")}
        </button>
        <button
          type="button"
          disabled={rowBusy}
          onClick={() => (rejectingId === doc.id ? cancelReject() : openReject(doc.id))}
          className="rounded-sm border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 max-md:min-h-11 max-md:text-sm"
        >
          {t("invoice_approvals_reject")}
        </button>
      </div>
      {rowErrors[doc.id] && <div className="mt-1 text-xs text-red-600">{rowErrors[doc.id]}</div>}
    </>
  );

  const renderRejectForm = (doc: InvoiceWaitingDoc, rowBusy: boolean) => (
    <>
      <label className="mb-1 block text-xs font-medium text-gray-600">
        {t("invoice_approvals_reject_reason")}
      </label>
      <textarea
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        rows={2}
        className="w-full rounded-sm border border-gray-300 px-2 py-1 text-base md:text-sm"
      />
      <div className="mt-2 flex items-center gap-2 max-md:gap-3">
        <button
          type="button"
          disabled={rowBusy || !rejectReason.trim()}
          onClick={() => handleConfirmReject(doc)}
          className="rounded-sm bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 max-md:min-h-11 max-md:text-sm"
        >
          {t("invoice_approvals_reject_confirm")}
        </button>
        <button
          type="button"
          disabled={rowBusy}
          onClick={cancelReject}
          className="rounded-sm border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 max-md:min-h-11 max-md:text-sm"
        >
          {t("invoice_approvals_cancel")}
        </button>
      </div>
    </>
  );

  const showSkeleton = isLoading && docs.length === 0;
  const showError = !showSkeleton && isError && docs.length === 0;
  const showEmpty = !showSkeleton && !showError && docs.length === 0;
  const showTable = !showSkeleton && docs.length > 0;

  return (
    <div className="p-4 space-y-4">
      <p className="text-sm text-gray-500">{t("invoice_approvals_intro")}</p>

      {showSkeleton && <DataTableSkeleton rows={4} columns={6} />}

      {showError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{t("invoice_approvals_load_failed")}</span>
          <button
            type="button"
            onClick={reload}
            className="rounded-sm border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            {t("try_again")}
          </button>
        </div>
      )}

      {showEmpty && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          <p>{t("invoice_approvals_empty")}</p>
          {companyId && (
            <Link
              to={`/app/${companyId}/invoice-inbox?tab=awaiting_approval`}
              className="mt-2 inline-block text-blue-600 hover:underline"
            >
              {t("invoice_approvals_go_register")}
            </Link>
          )}
        </div>
      )}

      {showTable && isPhone && (
        <ul className="space-y-3">
          {docs.map((doc) => {
            const rowBusy = busyIds.has(doc.id);
            return (
              <li key={doc.id} className="space-y-3 rounded-lg border border-gray-200 bg-white p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="break-words font-medium">{doc.supplierNameRaw || "—"}</div>
                    {doc.bankChangeFlagged && (
                      <div className="text-xs text-amber-600">{t("invoice_approvals_bank_change")}</div>
                    )}
                    {doc.duplicateOfDoc && (
                      <div className="text-xs text-red-600">{t("invoice_approvals_duplicate")}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      {doc.invoiceNumber || "—"} · {formatPrettyDate(doc.issueDate)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right font-semibold tabular-nums">
                    {formatAmount(doc.grossAmount, doc.currency)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                  <span>
                    {t("invoice_approvals_col_due")}: {formatPrettyDate(doc.dueDate)}
                  </span>
                  {isDueDatePast(doc.dueDate) && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-red-700">
                      {t("invoice_approvals_overdue")}
                    </span>
                  )}
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
                    {decisionLabel(doc.myRole)}
                  </span>
                </div>
                <div>{renderActions(doc, rowBusy)}</div>
                {rejectingId === doc.id && (
                  <div className="rounded-md bg-gray-50 p-3">{renderRejectForm(doc, rowBusy)}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {showTable && !isPhone && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4">{t("invoice_approvals_col_supplier")}</th>
                <th className="py-2 pr-4">{t("invoice_approvals_col_invoice")}</th>
                <th className="py-2 pr-4">{t("invoice_approvals_col_due")}</th>
                <th className="py-2 pr-4 text-right">{t("invoice_approvals_col_amount")}</th>
                <th className="py-2 pr-4">{t("invoice_approvals_col_role")}</th>
                <th className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => {
                const rowBusy = busyIds.has(doc.id);
                const overdue = isDueDatePast(doc.dueDate);
                return (
                  <Fragment key={doc.id}>
                    <tr className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-4">
                        <div>{doc.supplierNameRaw || "—"}</div>
                        {doc.bankChangeFlagged && (
                          <div className="text-xs text-amber-600">{t("invoice_approvals_bank_change")}</div>
                        )}
                        {doc.duplicateOfDoc && (
                          <div className="text-xs text-red-600">{t("invoice_approvals_duplicate")}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <div>{doc.invoiceNumber || "—"}</div>
                        <div className="text-xs text-gray-500">{formatPrettyDate(doc.issueDate)}</div>
                      </td>
                      <td className="py-2 pr-4">
                        <div>{formatPrettyDate(doc.dueDate)}</div>
                        {overdue && (
                          <span className="mt-0.5 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                            {t("invoice_approvals_overdue")}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {formatAmount(doc.grossAmount, doc.currency)}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                          {decisionLabel(doc.myRole)}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{renderActions(doc, rowBusy)}</td>
                    </tr>
                    {rejectingId === doc.id && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td colSpan={6} className="px-4 py-2">
                          {renderRejectForm(doc, rowBusy)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
